import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Plus } from "lucide-react";
import { api, type ApiResponse } from "@/api/client";
import { formatDate } from "@/lib/format";

interface WikiRow {
  id: string;
  title: string;
  category: string;
  status: string;
  destinationName: string | null;
  updatedAt: string;
}

/** Internal wiki / knowledge base list (Block 3 B3.7). */
export default function WikiPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["wiki"],
    queryFn: () => api.get<ApiResponse<WikiRow[]>>("/api/admin/wiki"),
  });
  const rows = data?.data || [];

  const categoryLabels: Record<string, string> = {
    general: t("content.wiki.categories.general"),
    playbook: t("content.wiki.categories.playbook"),
    contacts: t("content.wiki.categories.contacts"),
    sop: t("content.wiki.categories.sop"),
  };

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-gray-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t("content.wiki.list.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("content.wiki.list.subtitle")}
            </p>
          </div>
        </div>
        <Link to="/wiki/new" className="btn-primary">
          <Plus className="h-5 w-5" /> {t("content.wiki.list.newPage")}
        </Link>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              <th className="px-4 py-3">{t("common.fields.title")}</th>
              <th className="px-4 py-3">{t("common.fields.destination")}</th>
              <th className="px-4 py-3">{t("content.wiki.list.thCategory")}</th>
              <th className="px-4 py-3">{t("common.fields.status")}</th>
              <th className="px-4 py-3">{t("common.fields.updated")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                  {t("common.states.loading")}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                  {t("content.wiki.list.empty")}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2">
                    <Link
                      to={`/wiki/${r.id}`}
                      className="text-primary-600 dark:text-primary-400 font-medium hover:underline"
                    >
                      {r.title}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-gray-500">
                    {r.destinationName || t("content.wiki.list.generalFallback")}
                  </td>
                  <td className="px-4 py-2 text-gray-500">
                    {categoryLabels[r.category] || r.category}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        r.status === "published"
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      }`}
                    >
                      {statusLabels[r.status] || r.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-400">
                    {formatDate(r.updatedAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
